// What a chat's answer rests on, for the workspace showing its claims to list
// legibly: the sentences that cite the chat's claims ([c5], [c5, c6]), in the
// answer's own order, each with the uncited sentences just before it in its
// paragraph (a paragraph's headline often cites nothing itself).

const REF = /\[\s*(c\d+(?:\s*[,;/]\s*c\d+)*)\s*\]/gi;
const LIST = /^\s*(?:[-*+]|\d+[.)])\s+/;
const HEADING = /^\s*#{1,6}\s+/;
const QUOTE = /^\s*>\s?/;
const FENCE = /^\s*(```|~~~)/;
// Where a sentence ends: its stop, any closing quote or bracket, then space.
// Matched forward (no lookbehind: Safari before 16.4 lacks it).
const STOP = /[.!?]+["'”’)]*\s+/g;
const MAX_STEPS = 30;
const MAX_TEXT = 600;

const idsOf = (group) => group.split(/[,;/]/).map((s) => s.trim().toLowerCase()).filter(Boolean);

/** The claim refs in a run of text, where each stands and the claims it names. */
export function claimRefsIn(text) {
  const out = [];
  const s = String(text || "");
  REF.lastIndex = 0;
  let m;
  while ((m = REF.exec(s)) !== null) out.push({ start: m.index, end: m.index + m[0].length, ids: idsOf(m[1]) });
  return out;
}

// What a reader sees of a line of markdown: no markers, emphasis, code ticks
// or link targets.
function plain(line) {
  return line
    .replace(HEADING, "")
    .replace(QUOTE, "")
    .replace(LIST, "")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")
    .replace(/\*\*|__/g, "")
    .replace(/`+/g, "");
}

// A paragraph's lines joined; a list item, a heading or a quoted line starts
// one of its own. Fenced code is not prose and starts none.
function blocksOf(text) {
  const blocks = [];
  let cur = null;
  let fenced = false;
  for (const line of String(text || "").replace(/\r\n?/g, "\n").split("\n")) {
    if (FENCE.test(line)) { fenced = !fenced; cur = null; continue; }
    if (fenced) continue;
    if (!line.trim()) { cur = null; continue; }
    const starts = LIST.test(line) || HEADING.test(line) || QUOTE.test(line);
    if (!cur || starts) {
      cur = [];
      blocks.push(cur);
    }
    cur.push(plain(line).trim());
  }
  return blocks.map((b) => b.join(" "));
}

function sentencesOf(block) {
  const out = [];
  let from = 0;
  STOP.lastIndex = 0;
  let m;
  while ((m = STOP.exec(block)) !== null) {
    out.push(block.slice(from, m.index + m[0].length));
    from = m.index + m[0].length;
  }
  if (from < block.length) out.push(block.slice(from));
  return out;
}

const tidy = (s) => s.replace(REF, "").replace(/\s+/g, " ").replace(/\s+([.,;:!?])/g, "$1").trim();

/**
 * The steps an answer tells: `{ text, claims }`, `claims` the ids of the
 * claims its sentence cites, in the order cited. A ref standing after its
 * sentence's stop ("... UTC. [c5]") belongs to that sentence.
 */
export function answerSteps(text) {
  const steps = [];
  for (const block of blocksOf(text)) {
    const parts = [];
    for (const s of sentencesOf(block)) {
      const lead = s.match(/^\s*(?:\[\s*c\d+(?:\s*[,;/]\s*c\d+)*\s*\]\s*)+/i);
      if (lead && parts.length) {
        parts[parts.length - 1].refs.push(...claimRefsIn(lead[0]).flatMap((r) => r.ids));
        const rest = s.slice(lead[0].length);
        if (rest.trim()) parts.push({ text: rest, refs: claimRefsIn(rest).flatMap((r) => r.ids) });
        continue;
      }
      parts.push({ text: s, refs: claimRefsIn(s).flatMap((r) => r.ids) });
    }
    let lead = [];
    for (const p of parts) {
      if (!p.refs.length) { lead.push(p.text); continue; }
      const said = tidy([...lead, p.text].join(" "));
      lead = [];
      if (!said) continue;
      steps.push({ text: said.length > MAX_TEXT ? `${said.slice(0, MAX_TEXT - 3).trimEnd()}...` : said, claims: [...new Set(p.refs)] });
      if (steps.length >= MAX_STEPS) return steps;
    }
  }
  return steps;
}

/**
 * What a workspace lists of its chat's claims (`claims`: { id, n, statement,
 * status }): those the answer cites, in the order it first cites them, each in
 * the words of the sentence citing it (a second claim one sentence cites, in
 * its own); with no answer citing any of them, every claim in the order the
 * chat made it, in its own words.
 */
export function evidenceList(claims, answer = "") {
  const byId = new Map((claims || []).map((c) => [String(c.id).toLowerCase(), c]));
  const entry = (c, words, cited) => ({ id: String(c.id).toLowerCase(), n: c.n, words: String(words || c.statement || ""), statement: String(c.statement || ""), cited, status: c.status || null, at: Number.isFinite(c.at) ? c.at : null });
  const out = [];
  const seen = new Set();
  for (const s of answerSteps(answer)) {
    let first = true;
    for (const id of s.claims) {
      const c = byId.get(id);
      if (!c || seen.has(id)) continue;
      seen.add(id);
      out.push(entry(c, first ? s.text : c.statement, true));
      first = false;
    }
  }
  return out.length ? out : [...byId.values()].map((c) => entry(c, c.statement, false));
}

// A gap between two claims this long or longer is a break on the line the
// cards stand on, marked with how long it was.
export const BREAK_MS = 10 * 60_000;

/**
 * The listed claims in the order they happened (those at no known moment
 * last, in the order listed), each with `gap`: how long until the next one,
 * where that is a break (BREAK_MS or more), else null.
 */
export function inTimeOrder(list) {
  const timed = (list || []).filter((e) => Number.isFinite(e.at)).sort((a, b) => a.at - b.at || a.n - b.n);
  const rest = (list || []).filter((e) => !Number.isFinite(e.at));
  return [
    ...timed.map((e, i) => {
      const gap = i + 1 < timed.length ? timed[i + 1].at - e.at : null;
      return { ...e, gap: gap != null && gap >= BREAK_MS ? gap : null };
    }),
    ...rest.map((e) => ({ ...e, gap: null })),
  ];
}

/**
 * `list` in its own order (a walkthrough's moments, told in the order they
 * happened, some on messages the board has not read in and so at no moment
 * it knows), each with `gap` as inTimeOrder gives it, between neighbours
 * whose moments are known.
 */
export function inListedOrder(list) {
  const all = list || [];
  return all.map((e, i) => {
    const next = all[i + 1];
    const gap = next && Number.isFinite(e.at) && Number.isFinite(next.at) ? next.at - e.at : null;
    return { ...e, gap: gap != null && gap >= BREAK_MS ? gap : null };
  });
}

const NO_ANSWER = Object.freeze({ at: null, text: "" });

/**
 * The latest answer in a chat's transcript items: its text, and which answer
 * it is (`at`, its turn and its place in it, the same while it streams in),
 * else { at: null, text: "" }.
 */
export function latestAnswer(items) {
  for (let i = (items || []).length - 1; i >= 0; i -= 1) {
    const it = items[i];
    const kids = it?.kind === "turn" ? it.children || [] : [it];
    for (let j = kids.length - 1; j >= 0; j -= 1) if (kids[j]?.kind === "assistant" && kids[j].text) return { at: `${it?.id ?? i}:${j}`, text: kids[j].text };
  }
  return NO_ANSWER;
}
